import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import { FaCreditCard } from 'react-icons/fa'

import { getToken } from '../../lib/auth'
import { useMe } from '../../hooks/useMe'

import {
  getWalletSummary,
  quotePixePack,
  getPixePrice,
  reconcilePurchase,
  createPixePurchaseIntent,
  getPixePurchaseStatus,
  reconcilePixePurchase,
  type WalletSummary as WalletSummaryType,
} from '../../api/wallet'

import SummaryCard from '../../components/wallet/SummaryCard'

import AssetsList from '../../components/wallet/AssetsList'

import ReceiveModal from '../../components/wallet/ReceiveModal'

import SendModal from '../../components/wallet/SendModal'

import NFTGrid from '../../components/wallet/NFTGrid'

import ActivityList from '../../components/wallet/ActivityList'
import WalletTickets from '../../components/wallet/WalletTickets'
import WalletStaking from '../../components/wallet/WalletStaking'
import GiftToast from '../../components/gifts/GiftToast'
import PixeTokenIcon from '../../components/PixeTokenIcon'
import {
  BANNER_BODY,
  BANNER_TITLE,
  TOOLTIP_COPY,
  readBackupBannerDismissed,
  shouldShowBackupBanner,
  writeBackupBannerDismissed,
} from './walletBackup'
 

export const WALLET_TAB_ORDER = ['activity', 'assets', 'nfts', 'tickets', 'staking'] as const
export type WalletTab = (typeof WALLET_TAB_ORDER)[number]
type WalletView = 'wallet' | 'buy'
const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null

function isWalletTab(value: string | undefined | null): value is WalletTab {
  const normalized = String(value || '').trim().toLowerCase()
  return (WALLET_TAB_ORDER as readonly string[]).includes(normalized)
}

export function walletTabFromHash(hash: string | undefined | null): WalletTab {
  const normalized = String(hash || '').replace(/^#/, '').toLowerCase()
  if (isWalletTab(normalized)) {
    return normalized as WalletTab
  }
  return 'activity'
}

export function walletTabFromSearch(search: string | undefined | null): WalletTab | null {
  const params = new URLSearchParams(String(search || ''))
  const raw = params.get('tab')
  if (isWalletTab(raw)) return raw
  return null
}

export type WalletCheckoutStatus = 'success' | 'canceled' | null

export function walletCheckoutStatusFromSearch(search: string | undefined | null): WalletCheckoutStatus {
  const params = new URLSearchParams(String(search || ''))
  const statusValue = String(params.get('status') || '').trim().toLowerCase()
  if (statusValue === 'success') return 'success'
  if (statusValue === 'canceled') return 'canceled'
  if (params.get('success') === '1') return 'success'
  if (params.get('canceled') === '1') return 'canceled'
  return null
}

function walletTabFromLocation(search: string | undefined | null, hash: string | undefined | null): WalletTab {
  return walletTabFromSearch(search) ?? walletTabFromHash(hash)
}

function setWalletTabInUrl(tab: WalletTab) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  url.searchParams.set('tab', tab)
  if (isWalletTab(url.hash.replace(/^#/, ''))) {
    url.hash = ''
  }
  const next = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash || ''}`
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== next) {
    window.history.replaceState(window.history.state, '', next)
  }
}



export default function WalletSettings({
  initialView = 'wallet',
  buyCloseTo,
}: {
  initialView?: WalletView
  buyCloseTo?: string
} = {}) {

  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)

  const [error, setError] = useState<string | null>(null)

  const [summary, setSummary] = useState<WalletSummaryType | null>(null)

  const [msg, setMsg] = useState<string | null>(null)

  const [recOpen, setRecOpen] = useState(false)

  const [sendOpen, setSendOpen] = useState(false)

  const [copyMsg, setCopyMsg] = useState<string | null>(null)

  const [tab, setTab] = useState<WalletTab>('activity')

  const [view, setView] = useState<WalletView>(initialView)
  const [resumePaymentIntentId, setResumePaymentIntentId] = useState<string>('')

  const [price, setPrice] = useState<number | null>(null)

  const [actionMsg, setActionMsg] = useState<string | null>(null)

  const [toast, setToast] = useState<{ visible: boolean; emoji: string; label: string; detail?: string }>({
    visible: false,
    emoji: 'OK',
    label: '',
    detail: undefined,
  })

  const hideToast = useCallback(() => setToast((t) => ({ ...t, visible: false })), [])
  const showToast = useCallback((args: { emoji: string; label: string; detail?: string }) => {
    setToast({ visible: true, emoji: args.emoji, label: args.label, detail: args.detail })
  }, [])

  const onResult = useCallback((m: string) => {
    const msg = (m || '').trim()
    const lower = msg.toLowerCase()
    const emoji = lower.includes('fail') || lower.includes('error') ? '!' : lower.includes('processing') ? '...' : 'OK'
    showToast({ emoji, label: msg || 'Updated' })
    setActionMsg(msg || null)
    if (msg) setTimeout(() => setActionMsg(null), 1500)
  }, [showToast])
  const [backupBannerDismissed, setBackupBannerDismissed] = useState(false)
  const [backupTooltipOpen, setBackupTooltipOpen] = useState(false)

  const closeBuyView = useCallback(() => {
    if (buyCloseTo) {
      navigate(buyCloseTo)
      return
    }
    setView('wallet')
  }, [buyCloseTo, navigate])




  useEffect(() => {

    if (!authed) return

    setLoading(true)

    getWalletSummary()
      .then((data) => {
        const fixed = normalizeWallet(data)
        setSummary(fixed)
      })

      .catch((e) => setError(e?.message || 'Failed to load wallet'))

      .finally(() => setLoading(false))

  }, [authed])

  useEffect(() => {
    setBackupBannerDismissed(readBackupBannerDismissed(me?.id))
  }, [me?.id])

  useEffect(() => {
    if (typeof window === 'undefined') return
    setTab(walletTabFromLocation(window.location.search, window.location.hash))
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const nextView = String(url.searchParams.get('view') || '').trim().toLowerCase()
    const pi = String(url.searchParams.get('pi') || '').trim()
    if (nextView === 'buy') {
      setView('buy')
    }
    if (pi) {
      setResumePaymentIntentId(pi)
    }
  }, [])

  useEffect(() => {
    setWalletTabInUrl(tab)
  }, [tab])

  // Stripe Checkout return handler: show toast and poll server-truth until the ledger is updated.
  useEffect(() => {
    if (!authed) return

    const url = new URL(window.location.href)
    const qs = url.searchParams
    const checkoutSessionId = (qs.get('checkout_session_id') || '').trim()
    const checkoutStatus = walletCheckoutStatusFromSearch(url.search)
    const success = checkoutStatus === 'success'
    const canceled = checkoutStatus === 'canceled'

    if (!checkoutSessionId && !success && !canceled) return

    if (canceled) {
      showToast({ emoji: '!', label: 'Payment canceled', detail: 'No charges were made.' })
    } else {
      showToast({ emoji: '...', label: 'Payment processing', detail: 'Finalizing your PIXE credits...' })
    }

    let cancelled = false
    let tries = 0

    const clearQs = () => {
      qs.delete('checkout_session_id')
      qs.delete('success')
      qs.delete('canceled')
      qs.delete('status')
      const next = url.pathname + (qs.toString() ? `?${qs.toString()}` : '')
      window.history.replaceState({}, '', next)
    }

    const refreshNow = async () => {
      const d = await getWalletSummary()
      const fixed = normalizeWallet(d)
      setSummary(fixed)
    }

    const poll = async () => {
      if (!checkoutSessionId || canceled) {
        clearQs()
        return
      }

      tries += 1
      try {
        const res = await reconcilePurchase(checkoutSessionId)
        if (cancelled) return
        if (res.status === 'ok') {
          await refreshNow()
          showToast({ emoji: 'OK', label: 'Payment complete', detail: 'Balance updated.' })
          clearQs()
          return
        }
      } catch {
        // ignore reconcile errors; keep polling briefly
      }

      if (tries >= 30 && !cancelled) {
        showToast({ emoji: '...', label: 'Payment processing', detail: 'This can take a moment. Try Refresh if needed.' })
        clearQs()
        return
      }
    }

    void poll()
    const id = window.setInterval(poll, 2000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [authed, showToast])



  useEffect(() => {

    getPixePrice().then(setPrice).catch(()=>{})

  }, [])



  const fiatEstimate = summary?.summary?.fiat_estimate

  const pixeBalance = summary?.summary?.pixe_balance

  const changePct = summary?.summary?.change_24h_pct

  const assets = useMemo(() => summary?.assets || [], [summary])
  const walletEnabled = Boolean(summary?.summary?.address)
  const backupConfirmedAt = summary?.summary?.backup_confirmed_at || null
  const showBackupBanner = shouldShowBackupBanner({
    hasSummary: Boolean(summary),
    walletEnabled,
    view,
    backupConfirmedAt,
    dismissed: backupBannerDismissed,
  })

  function dismissBackupBanner() {
    writeBackupBannerDismissed(me?.id)
    setBackupBannerDismissed(true)
  }



  function onCopy() {

    if (!summary?.summary?.address) return

    navigator.clipboard.writeText(summary.summary.address).then(() => {

      setCopyMsg('Address copied')

      setTimeout(()=>setCopyMsg(null), 1200)

    }).catch(()=>{})

  }



  async function onReconcile() {

    setLoading(true)

    setMsg(null)

    try {

      // Server-truth refresh: does NOT do on-chain "set balance" reconcile.
      // (That can undo in-app ledger transactions like gifts.)
      await reconcilePurchase()
      await refreshSummary()
      setMsg('Refreshed wallet')

    } catch (e: any) {

      setMsg(e?.response?.data?.detail || e?.message || 'Refresh failed')

    } finally {

      setLoading(false)

    }

  }



  async function refreshSummary() {

    try {

      const d = await getWalletSummary()
      const fixed = normalizeWallet(d)
      setSummary(fixed)

    } catch {}

  }



  // Swap/Sell are intentionally disabled for now (buildout in progress).
  const title = view === 'wallet' ? 'Wallet' : 'Buy'

  return (

    <div className="space-y-6">
      <GiftToast visible={toast.visible} emoji={toast.emoji} label={toast.label} detail={toast.detail} onHide={hideToast} timeoutMs={2200} />

      <div className="flex items-center justify-between gap-3">

        <h1 className="text-xl font-extrabold">{title}</h1>

        <div className="flex items-center gap-2">

          {view === 'wallet' ? (

            <>

              <button
                onClick={() => setView('buy')}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sky-500/40 bg-sky-600/20 text-sky-100 hover:bg-sky-600/30 text-sm font-semibold"
              >
                <FaCreditCard />
                Buy
              </button>

              {/*
              <HeaderActionSwap active={view==='swap'} onClick={()=> setView('swap')} />
              <HeaderActionSell active={view==='sell'} onClick={()=> setView('sell')} />
              */}

            </>

          ) : (

            <button onClick={closeBuyView} className="px-3 py-1.5 rounded-full border border-white/15 bg-white/10 text-white/90 hover:bg-white/15 text-sm font-semibold">Close</button>

          )}

        </div>

      </div>

      {loading && <div className="rounded-xl border border-white/10 p-4">Loading...</div>}

      {error && <div className="rounded-xl border border-white/10 p-4 text-red-400">{error}</div>}

      {summary && view==='wallet' && (

        <div className="space-y-6">

          <SummaryCard fiatEstimate={fiatEstimate} pixeBalance={pixeBalance} change24hPct={changePct} ss58={summary.summary?.address} onReceive={()=>setRecOpen(true)} onCopy={onCopy} onSend={()=> setSendOpen(true)} />

          {copyMsg && <div className="text-xs text-white/80">{copyMsg}</div>}

          {actionMsg && <div className="text-sm text-white/80">{actionMsg}</div>}

          {msg && <div className="text-sm text-white/80">{msg}</div>}

          {sendOpen && (

            <SendModal

              open={sendOpen}

              onClose={()=> setSendOpen(false)}

              onSent={(res)=>{

                setActionMsg('Send submitted')

                setSendOpen(false)

                refreshSummary()

                setTimeout(()=>setActionMsg(null), 1500)

              }}

            />

          )}

          <div className="flex items-center justify-between">

            <div className="inline-flex items-center rounded-lg border border-white/10 bg-white/5 p-1">

              <button data-testid="wallet-tab-activity" onClick={()=>setTab('activity')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab==='activity' ? 'bg-white text-neutral-950' : 'text-white/85 hover:bg-white/10'}`}>Activity</button>

              <button data-testid="wallet-tab-assets" onClick={()=>setTab('assets')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab==='assets' ? 'bg-white text-neutral-950' : 'text-white/85 hover:bg-white/10'}`}>Assets</button>

              <button data-testid="wallet-tab-handles" onClick={()=>setTab('nfts')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab==='nfts' ? 'bg-white text-neutral-950' : 'text-white/85 hover:bg-white/10'}`}>Handles</button>

              <button data-testid="wallet-tab-tickets" onClick={()=>setTab('tickets')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab==='tickets' ? 'bg-white text-neutral-950' : 'text-white/85 hover:bg-white/10'}`}>Tickets</button>

              <button data-testid="wallet-tab-staking" onClick={()=>setTab('staking')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${tab==='staking' ? 'bg-white text-neutral-950' : 'text-white/85 hover:bg-white/10'}`}>Staking</button>

            </div>

            {tab==='assets' && (

              <div className="flex items-center gap-2">

                <button onClick={onReconcile} className="px-3 py-1.5 rounded-lg border border-white/15 text-white/90 hover:bg-white/10">Refresh</button>

              </div>

            )}

          </div>

          {tab==='activity' && <ActivityList />}

          {tab==='assets' && <AssetsList assets={assets as any} onSendPixe={()=> setSendOpen(true)} />}

          {tab==='nfts' && <NFTGrid address={summary.summary?.address} />}

          {tab==='tickets' && <WalletTickets />}

          {tab==='staking' && <WalletStaking />}

          {/* Security prompts are intentionally rendered after tab content. */}
          {showBackupBanner && (
            <section className="mt-4 space-y-3">
              <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-white">Protect Your Wallet</div>
                      <button
                        type="button"
                        aria-label="Backup disclaimer"
                        onFocus={() => setBackupTooltipOpen(true)}
                        onBlur={() => setBackupTooltipOpen(false)}
                        onMouseEnter={() => setBackupTooltipOpen(true)}
                        onMouseLeave={() => setBackupTooltipOpen(false)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/20 text-[11px] text-white/80 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
                      >
                        ?
                      </button>
                    </div>
                    {backupTooltipOpen && (
                      <div role="tooltip" className="absolute left-0 top-7 z-20 w-80 rounded-lg border border-white/15 bg-neutral-900 p-3 text-xs text-white/80 shadow-lg">
                        {TOOLTIP_COPY}
                      </div>
                    )}
                  </div>
                  <span className="inline-flex items-center rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-200">
                    {BANNER_TITLE}
                  </span>
                </div>
                <div className="mt-2 text-sm text-amber-50/90">{BANNER_BODY}</div>
                <div className="mt-3 flex items-center gap-2">
                  <Link
                    to="/settings/security#recovery-kit"
                    className="px-3 py-1.5 rounded-lg border border-amber-300/30 bg-amber-400/20 text-amber-100 text-sm font-semibold hover:bg-amber-400/30"
                  >
                    Create Backup
                  </Link>
                  <button
                    type="button"
                    onClick={dismissBackupBanner}
                    className="px-3 py-1.5 rounded-lg border border-white/15 text-white/80 text-sm hover:bg-white/10"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </section>
          )}



        </div>

      )}



      {view==='buy' && (
        <BuyForm
          mode="page"
          onClose={closeBuyView}
          onResult={onResult}
          price={price}
          refreshSummary={refreshSummary}
          resumePaymentIntentId={resumePaymentIntentId}
          onResumeHandled={() => {
            setResumePaymentIntentId('')
            if (typeof window === 'undefined') return
            const url = new URL(window.location.href)
            url.searchParams.delete('view')
            url.searchParams.delete('pi')
            const next = `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''}${url.hash || ''}`
            window.history.replaceState({}, '', next)
          }}
        />
      )}

      {/*
      {view==='swap' && (
        <SwapForm
          mode="page"
          onClose={()=> setView('wallet')}
          onResult={onResult}
          price={price}
          balances={{
            USD: Number((assets.find(a=>a.symbol==='USD')?.balance as any) ?? 0),
            PIXE: Number((assets.find(a=>a.symbol==='PIXE')?.balance as any) ?? 0),
          }}
        />
      )}

      {view==='sell' && (
        <SellForm mode="page" onClose={()=> setView('wallet')} onResult={onResult} price={price} />
      )}
      */}



      <ReceiveModal open={recOpen} onClose={()=>setRecOpen(false)} address={summary?.summary?.address} />

    </div>

  )

}



function BuyForm({
  onClose,
  onResult,
  price,
  mode = 'inline',
  refreshSummary,
  resumePaymentIntentId,
  onResumeHandled,
}: {
  onClose: () => void
  onResult: (msg: string) => void
  price: number | null
  mode?: 'inline' | 'page'
  refreshSummary: () => Promise<void>
  resumePaymentIntentId?: string
  onResumeHandled?: () => void
}) {
  const PACKS = [
    { usd: 10, pixe: 10 },
    { usd: 25, pixe: 25 },
    { usd: 100, pixe: 100 },
  ] as const

  const [pack, setPack] = useState<(typeof PACKS)[number]>(PACKS[0])
  const packRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [billingEmail, setBillingEmail] = useState('')
  const [quote, setQuote] = useState<{ base_cents: number; fee_cents: number; total_cents: number } | null>(null)
  const [intent, setIntent] = useState<{
    client_secret: string
    payment_intent_id: string
    amount_cents: number
    fee_cents: number
    total_cents: number
    currency: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creatingIntent, setCreatingIntent] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [purchaseState, setPurchaseState] = useState<'idle' | 'processing' | 'credited' | 'pending' | 'failed'>('idle')
  const [creditedAmount, setCreditedAmount] = useState(0)
  const [newBalance, setNewBalance] = useState<number | null>(null)
  const [statusText, setStatusText] = useState<string | null>(null)
  const [successText, setSuccessText] = useState<string | null>(null)
  const billingEmailRef = useRef('')
  const successRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    billingEmailRef.current = billingEmail
  }, [billingEmail])

  useEffect(() => {
    setSuccessText(null)
    setError(null)
    setPurchaseState('idle')
    setCreditedAmount(0)
    setNewBalance(null)
  }, [pack.usd])

  useEffect(() => {
    if (purchaseState === 'credited') {
      successRef.current?.focus()
    }
  }, [purchaseState])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const q = await quotePixePack(pack.pixe as 10 | 25 | 100)
        if (cancelled) return
        setQuote({ base_cents: q.base_cents, fee_cents: q.fee_cents, total_cents: q.total_cents })
      } catch (e: any) {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Unable to quote processing fee.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [pack.pixe])

  const createIntent = useCallback(async () => {
    setCreatingIntent(true)
    setError(null)
    setStatusText(null)
    setPurchaseState('idle')
    try {
      const res = await createPixePurchaseIntent({
        pack: pack.pixe as 10 | 25 | 100,
        email: billingEmailRef.current.trim() || undefined,
      })
      setIntent(res)
      return res
    } catch (e: any) {
      setIntent(null)
      setPurchaseState('failed')
      setError(e?.response?.data?.detail || e?.message || 'Unable to initialize payment.')
      return null
    } finally {
      setCreatingIntent(false)
    }
  }, [pack.pixe])

  useEffect(() => {
    void createIntent()
  }, [createIntent])

  function fmtUsdFromCents(cents?: number) {
    const v = typeof cents === 'number' && Number.isFinite(cents) ? cents : 0
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(v / 100)
  }

  function selectPack(nextIndex: number) {
    const idx = ((nextIndex % PACKS.length) + PACKS.length) % PACKS.length
    setPack(PACKS[idx])
    window.requestAnimationFrame(() => packRefs.current[idx]?.focus())
  }

  const markCredited = useCallback(async (amount: number) => {
    await refreshSummary()
    try {
      const raw = await getWalletSummary()
      const fixed = normalizeWallet(raw)
      setNewBalance(Number(fixed?.summary?.pixe_balance ?? 0))
    } catch {
      setNewBalance(null)
    }
    setPurchaseState('credited')
    setCreditedAmount(Math.max(0, Number(amount || 0)))
    const msg = `Added ${Math.max(0, Number(amount || 0))} PIXE to your wallet`
    setSuccessText(msg)
    setStatusText(null)
    setError(null)
    onResult(msg)
  }, [onResult, refreshSummary])

  const pollPurchase = useCallback(async (paymentIntentId: string) => {
    setPurchaseState('processing')
    setStatusText('Finalizing purchase...')
    for (let i = 0; i < 30; i += 1) {
      try {
        const statusRes = await getPixePurchaseStatus(paymentIntentId)
        if (statusRes.credited || statusRes.status === 'succeeded') {
          await markCredited(Number(statusRes.pack_pixe || pack.pixe || 0))
          return true
        }
        if (statusRes.status === 'failed') {
          setPurchaseState('failed')
          setStatusText(null)
          setError('Payment failed. Please try again.')
          return false
        }
        setPurchaseState('pending')
      } catch (e: any) {
        setPurchaseState('failed')
        setError(e?.response?.data?.detail || e?.message || 'Unable to verify payment status.')
        setStatusText(null)
        return false
      }
      await new Promise((resolve) => setTimeout(resolve, 2000))
    }
    setPurchaseState('pending')
    setStatusText('Payment succeeded, credit pending. Refresh wallet in a moment.')
    return false
  }, [markCredited, pack.pixe])

  const runReconcile = useCallback(async (paymentIntentId: string) => {
    setPurchaseState('processing')
    try {
      const reconciled = await reconcilePixePurchase(paymentIntentId)
      if (reconciled.status === 'credited') {
        const credited = Number(reconciled.credited_pixe || pack.pixe || 0)
        await markCredited(credited)
        return true
      }
      if (reconciled.status === 'pending') {
        setPurchaseState('pending')
        setStatusText(reconciled.message || 'Finalizing purchase...')
        return false
      }
      if (reconciled.status === 'failed') {
        setPurchaseState('failed')
        setError(reconciled.message || 'Unable to credit purchase yet.')
        return false
      }
      setPurchaseState('pending')
      return false
    } catch (e: any) {
      setPurchaseState('failed')
      setError(e?.response?.data?.message || e?.response?.data?.detail || e?.message || 'Unable to reconcile payment.')
      return false
    }
  }, [markCredited, pack.pixe])

  useEffect(() => {
    const pi = String(resumePaymentIntentId || '').trim()
    if (!pi) return
    void (async () => {
      await pollPurchase(pi)
      if (onResumeHandled) onResumeHandled()
    })()
  }, [onResumeHandled, pollPurchase, resumePaymentIntentId])

  const handleConfirm = useCallback(async (stripe: any, elements: any) => {
    const activeIntent = intent ?? await createIntent()
    if (!activeIntent) return
    setConfirming(true)
    setError(null)
    setStatusText(null)
    setPurchaseState('processing')
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const returnUrl = `${origin}/settings/wallet?view=buy&pi=${encodeURIComponent(activeIntent.payment_intent_id)}`
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      })
      if (result.error) {
        setPurchaseState('failed')
        setError(result.error.message || 'Payment confirmation failed.')
        return
      }
      const credited = await runReconcile(activeIntent.payment_intent_id)
      if (!credited) {
        await pollPurchase(activeIntent.payment_intent_id)
      }
    } finally {
      setConfirming(false)
    }
  }, [createIntent, intent, pollPurchase, runReconcile])

  const handleViewActivity = useCallback(() => {
    onClose()
    setWalletTabInUrl('activity')
  }, [onClose])

  const handleBuyMore = useCallback(async () => {
    setPurchaseState('idle')
    setSuccessText(null)
    setStatusText(null)
    setError(null)
    setCreditedAmount(0)
    setNewBalance(null)
    setIntent(null)
    setBillingEmail('')
    setPack(PACKS[0])
    await createIntent()
  }, [createIntent])

  return (
    <div className={`rounded-xl border border-white/10 bg-white/5 p-4 ${mode === 'page' ? 'mx-auto max-w-2xl w-full' : ''}`}>
      {mode === 'inline' && <div className="text-white/85 font-semibold mb-2">Buy PIXE</div>}

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-white/80 font-semibold">Choose a pack</div>
        <div role="radiogroup" aria-label="PIXE packs" className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PACKS.map((p, idx) => {
            const active = p.usd === pack.usd
            const tag = p.pixe === 25 ? 'Popular' : p.pixe === 100 ? 'Best value' : ''
            return (
              <button
                key={p.usd}
                ref={(el) => { packRefs.current[idx] = el }}
                type="button"
                role="radio"
                aria-checked={active}
                tabIndex={active ? 0 : -1}
                onClick={() => selectPack(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); selectPack(idx + 1); return }
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); selectPack(idx - 1); return }
                  if (e.key === 'Home') { e.preventDefault(); selectPack(0); return }
                  if (e.key === 'End') { e.preventDefault(); selectPack(PACKS.length - 1); return }
                }}
                className={[
                  'group relative rounded-xl border px-3 py-3 text-left transition outline-none overflow-hidden',
                  'focus-visible:ring-2 focus-visible:ring-sky-400/40 focus-visible:ring-offset-0',
                  active
                    ? 'border-sky-400/55 bg-gradient-to-b from-sky-500/15 to-black/30 ring-1 ring-sky-400/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
                    : 'border-white/10 bg-gradient-to-b from-white/5 to-black/35 hover:from-white/8 hover:to-black/25 hover:border-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
                ].join(' ')}
              >
                {tag ? (
                  <span className="absolute top-2 right-2 rounded-full border border-white/12 bg-black/35 px-2 py-0.5 text-[11px] font-semibold text-white/85 backdrop-blur">
                    {tag}
                  </span>
                ) : null}

                <div className="text-[12px] text-white/65">You get</div>

                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold tracking-tight text-white">{p.pixe}</span>
                  <span className="text-lg font-extrabold text-white">PIXE</span>
                  <PixeTokenIcon size={36} ariaLabel="PIXE token" className="opacity-95" />
                </div>

                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-white/70">${p.usd}</span>
                  <span className="rounded-full border border-white/12 bg-black/25 px-2 py-0.5 text-[11px] font-semibold text-white/75">
                    PIXE
                  </span>
                </div>
              </button>
            )
          })}
        </div>

        <div className="mt-4">
          <div className="text-xs font-semibold text-white/70 uppercase tracking-wide">Email (optional)</div>
          <input
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            placeholder="you@domain.com"
            className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/50 outline-none focus:ring-2 focus:ring-sky-500/40"
          />
          <div className="mt-2 text-xs text-white/55">
            PIXE credits are in-app and non-transferable. Payouts are made in USD.
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-white/80 font-semibold">Payment details</div>

        <div className="mt-2 text-sm text-white/75">
          {intent ? (
            <span>
              {fmtUsdFromCents(intent.amount_cents)} + {fmtUsdFromCents(intent.fee_cents)} processing fee
              <span className="text-white/55"> (total {fmtUsdFromCents(intent.total_cents)})</span>
            </span>
          ) : quote ? (
            <span>
              {fmtUsdFromCents(quote.base_cents)} + {fmtUsdFromCents(quote.fee_cents)} processing fee
              <span className="text-white/55"> (total {fmtUsdFromCents(quote.total_cents)})</span>
            </span>
          ) : (
            <span className="text-white/55">Calculating processing fee...</span>
          )}
        </div>

        <div className="mt-3 space-y-3">
          {purchaseState === 'credited' ? (
            <div
              ref={successRef}
              tabIndex={-1}
              aria-live="polite"
              className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 outline-none"
            >
              <div className="mb-2 text-2xl text-emerald-300" aria-hidden="true">{'\u2713'}</div>
              <div className="text-lg font-bold text-emerald-100">Added {creditedAmount} PIXE to your wallet</div>
              <div className="mt-1 text-sm text-emerald-200/90">
                New balance: {newBalance != null ? `${newBalance} PIXE` : 'Updated'}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={handleViewActivity}
                  className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/90 hover:bg-white/10"
                >
                  View Activity
                </button>
                <button
                  type="button"
                  onClick={() => void handleBuyMore()}
                  className="rounded-lg border border-emerald-500/40 px-3 py-2 text-sm text-emerald-100 hover:bg-emerald-500/20"
                >
                  Buy More
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/90 hover:bg-white/10"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              {!stripePromise ? (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                  Stripe is not configured right now.
                </div>
              ) : intent?.client_secret ? (
                <Elements stripe={stripePromise} options={{ clientSecret: intent.client_secret, appearance: { theme: 'night' as const } }}>
                  <PixePaymentForm
                    totalCents={intent.total_cents}
                    busy={confirming || creatingIntent}
                    onSubmit={handleConfirm}
                  />
                </Elements>
              ) : (
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-sm text-white/65">
                  {creatingIntent ? 'Initializing payment...' : 'Select a pack to continue.'}
                </div>
              )}
              {successText ? (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">{successText}</div>
              ) : null}
              {statusText ? <div className="text-sm text-white/75">{statusText}</div> : null}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const pi = String(intent?.payment_intent_id || resumePaymentIntentId || '').trim()
                    if (!pi) return
                    void (async () => {
                      const credited = await runReconcile(pi)
                      if (!credited) await pollPurchase(pi)
                    })()
                  }}
                  disabled={confirming}
                  className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/85 hover:bg-white/10 disabled:opacity-50"
                >
                  Check status
                </button>
                <button
                  type="button"
                  onClick={() => void createIntent()}
                  disabled={creatingIntent || confirming}
                  className="rounded-lg border border-white/15 px-3 py-2 text-sm text-white/85 hover:bg-white/10 disabled:opacity-50"
                >
                  Refresh payment
                </button>
                {mode === 'inline' ? (
                  <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10">
                    Close
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>

        {error ? <div className="mt-3 text-sm text-rose-300">{error}</div> : null}
      </div>
    </div>
  )
}

function PixePaymentForm({
  totalCents,
  busy,
  onSubmit,
}: {
  totalCents: number
  busy: boolean
  onSubmit: (stripe: any, elements: any) => Promise<void>
}) {
  const stripe = useStripe()
  const elements = useElements()

  const label = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format((totalCents || 0) / 100)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements || busy) return
    await onSubmit(stripe, elements)
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || !elements || busy}
        className="w-full rounded-lg border border-sky-500/40 bg-sky-600 text-white font-extrabold py-3 hover:bg-sky-500 disabled:opacity-60"
      >
        {busy ? 'Processing...' : `Pay ${label}`}
      </button>
      <div className="text-xs text-white/55">Powered by Stripe</div>
    </form>
  )
}

/*
  Swap/Sell are intentionally disabled for now.
  Keeping the implementation commented out makes it easy to restore later without exposing the UI.
*/
// ---- Local helpers to normalize any mis-scaled PIXE payloads ----
function toNum(v: any): number { return typeof v === 'number' ? v : Number(String(v ?? '0').replace(/,/g, '')) }
function normalizePixe(n: any): number {
  const x = toNum(n)
  if (!Number.isFinite(x)) return 0
  // Heuristic: values >= 1e12 are likely micro-PIXE (x1e6); scale down
  return x >= 1e12 ? x / 1e6 : x
}
function normalizeWallet(raw: WalletSummaryType): WalletSummaryType {
  try {
    const assets = (raw.assets || []).map((a) => {
      if (a.symbol === 'PIXE') {
        const bal = normalizePixe(a.balance)
        const val = normalizePixe(a.fiat_value)
        return { ...a, balance: bal, fiat_value: val }
      }
      return { ...a, balance: toNum(a.balance), fiat_value: toNum(a.fiat_value) }
    })
    const pixe = assets.find((a) => a.symbol === 'PIXE')
    const usd = assets.find((a) => a.symbol === 'USD')
    const pixeBal = normalizePixe(pixe?.balance ?? 0)
    const fiatEst = toNum(pixe?.fiat_value ?? 0) + toNum(usd?.fiat_value ?? 0)
    return {
      summary: {
        fiat_estimate: fiatEst,
        pixe_balance: pixeBal,
        change_24h_pct: toNum(raw.summary?.change_24h_pct ?? 0),
        address: raw.summary?.address || '',
        backup_confirmed_at: raw.summary?.backup_confirmed_at ?? null,
        last_recovery_kit_generated_at: raw.summary?.last_recovery_kit_generated_at ?? null,
      },
      assets,
    }
  } catch {
    return raw
  }
}




